import React from 'react'
import getRoutes from '@/lib/getRoutes'
import Link from '@/components/Common/Link'
import ProductImg from '@/components/Product/ProductImg'
import ProductQtyInput from '@/components/Product/ProductQtyInput'
import useProduct from '@/lib/hook/useProduct'
import { useMediaQuery, useTheme } from '@mui/material'
import CompanyName from '../CompanyName'
import BrandNameLink from '@/components/Brands/BrandNameLink'
import Highlight from '@/components/Search/Highlight'

type Props = { hit: any; variant?: 'card' | 'row' }

const ProductCard = ({ hit, variant = 'card' }: Props) => {
  const product = hit
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const slug = String(product?.seo_name ?? '')
  const { product: fullProduct, loading: loadingProduct } = useProduct(slug)

  const urlProduct = getRoutes({
    route: 'Product',
    values: { '[slug]': product.seo_name },
  })

  const isRow = variant === 'row'

  const precommandeBlock = product.precommande === 'Y' && (
    <div className="flex flex-col items-end mt-1 text-right leading-tight">
      {!!product.avail_since && product.avail_since > 0 && (
        <span className="text-xs text-amber-700 italic">
          {t({ id: 'web_precommande_delivery_on' }).replace(
            '{date}',
            new Date(product.avail_since * 1000).toLocaleDateString('fr-FR')
          )}
        </span>
      )}
      <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
        {t({ id: 'web_precommande_only' })}
      </span>
    </div>
  )

  const getLayout = (image, title, qty) => {
    if (isMobile)
      return (
        <div className="w-full max-w-full overflow-hidden">
          <div className="flex items-center min-w-0">
            {image}
            <div className="min-w-0">{title}</div>
          </div>
          <div className="flex justify-end mt-2 w-full">
            <div className="w-full flex flex-col items-stretch">
              {qty}
              {precommandeBlock}
            </div>
          </div>
        </div>
      )

    return (
      <div className={'flex items-center'}>
        {image}

        <div className={'w-full mx-8'}>{title}</div>

        <div className="flex flex-col items-end">
          {qty}
          {precommandeBlock}
        </div>
      </div>
    )
  }

  const image = (
    <Link href={urlProduct.href} as={urlProduct.as} data-testid={'viewProduct'}>
      <div className={'w-12 mr-3 shrink-0'}>
        <ProductImg product={product} size={'small'} />
      </div>
    </Link>
  )

  const title = (
    <>
      <div className="flex items-center gap-1">
        <BrandNameLink product={product} enableHighlight />
        {product.product_code && (
          <div className="text-xs">
            - EAN : <Highlight hit={product} attribute="product_code" />
          </div>
        )}
      </div>
      <Link href={urlProduct.href} as={urlProduct.as}>
        <div
          title={product.product}
          className={isRow ? 'aa-ProductRow-Title' : 'aa-ProductCard-Title'}
        >
          <Highlight hit={product} attribute="product" />
        </div>
      </Link>

      {product.company_name && (
        <div className="text-xs flex gap-1">
          Vendu par : <CompanyName object={product} />
        </div>
      )}
    </>
  )

  const qty = !loadingProduct && fullProduct && (
    // Mobile (panneau autocomplete etroit) : mode compact sans bandeau prix,
    // sinon le rang prix+selecteur+panier deborde de l'ecran
    <ProductQtyInput
      product={fullProduct}
      showAddButton={true}
      compact={isMobile}
      showInfoBar={!isMobile}
    />
  )

  return getLayout(image, title, qty)
}

export default ProductCard
